import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Skeleton,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment
} from '@mui/material';
import ColorSelector from '../../components/ColorSelector';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { multiTenantMembershipAPI } from '../../services/api';
import ModernCard from '../../components/Common/ModernCard';
import ModernButton from '../../components/Common/ModernButton';
import ModernTextField from '../../components/Common/ModernTextField';
import IconContainer from '../../components/Common/IconContainer';
import { useAuth } from '../../contexts/AuthContext';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import useResponsive from '../../hooks/useResponsive';
import { logger } from '../../utils/logger';

interface MembershipCard {
  id: string;
  name: string;
  tier_level: number;
  points_threshold: number;
  benefits: string;
  member_count: number;
  status: 'active' | 'inactive';
  created_at: string;
  // Company information for multi-tenancy
  template_company_id?: string;
  company_name?: string;  // Company name from backend
  // External template ID for this tier
  pass_template_id?: string;
  // Legacy fields for CRUD operations compatibility
  membership_type?: string;
  total_passes?: number;
}

const MembershipCards: React.FC = () => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();

  // State management
  const [cards, setCards] = useState<MembershipCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCard, setEditingCard] = useState<MembershipCard | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string>('customer');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingCard, setDeletingCard] = useState<MembershipCard | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Menu state for three dots
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCard, setSelectedCard] = useState<MembershipCard | null>(null);

  // Color state for form
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');
  const [labelColor, setLabelColor] = useState('#666666');
  const [stripColor, setStripColor] = useState('#0066cc');

  // Template customization state
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [sourceTierId, setSourceTierId] = useState<string>('');

  // Store colors for each tier (key: tierID_companyID for unique identification)
  const [tierColors, setTierColors] = useState<{[tierKey: string]: any}>({});

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompanyId, setFilterCompanyId] = useState<string>('');

  // Fetch membership tiers from Backend API
  const fetchCards = useCallback(async () => {
    try {
      setLoading(true);
      logger.log('[MembershipCards] Fetching membership tiers from Company API...');

      // Get membership tiers from new Company API (loads all tiers for company automatically)
      const apiResponse = await multiTenantMembershipAPI.getTemplateTiers();
      logger.log('[MembershipCards] Company API Response:', apiResponse);
      logger.log('[MembershipCards] Company ID from API:', apiResponse.company_id);

      const tiersData = apiResponse.data || apiResponse.tiers || [];
      logger.log('[MembershipCards] Tiers extracted:', tiersData);

      if (tiersData.length === 0) {
        logger.log('[MembershipCards] No membership tiers returned from Company API');
        setCards([]);
        setError('Nisu pronađeni nivoi članstva. Molimo proverite da li su programi povezani sa vašom kompanijom.');
        return;
      }

      // Transform API data to match our interface - tiersData is CompanyTier[]
      const transformedCards: MembershipCard[] = tiersData.map((tier: any) => ({
        id: tier.id,
        name: tier.name,
        tier_level: tier.tier_level,
        points_threshold: tier.points_threshold || 0,
        benefits: tier.benefits || '',
        member_count: tier.member_count || 0,
        status: 'active', // Tiers are always active
        created_at: tier.created_at || new Date().toISOString(),
        // Add company information from template/tier
        template_company_id: tier.template_company_id || apiResponse.template_company_id,
        company_name: tier.company_name, // Include company name from backend
        // Legacy fields for compatibility
        membership_type: `tier_${tier.tier_level}`,
        total_passes: tier.member_count || 0
      }));

      setCards(transformedCards);
      setError(null);
      logger.log('[MembershipCards] Membership tiers loaded successfully from Backend API:', transformedCards);
      logger.log('[MembershipCards] First tier template_company_id:', transformedCards[0]?.template_company_id);

      // Load colors for all tiers
      await loadAllTierColors(transformedCards);

    } catch (error) {
      logger.error('[MembershipCards] Error fetching membership tiers from Backend API:', error);
      setError('Failed to load membership tiers from Backend API. Please check backend connection.');
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);



  // Companies loading is handled by the state dependency system - no manual refresh needed

  // Update company selection when companies load and dialog is open
  useEffect(() => {
    if (companies.length > 0 && editingCard && userRole === 'admin' && !selectedCompanyId) {
      logger.log('🔄 Companies loaded, setting company for open dialog');

      if (editingCard.template_company_id) {
        setSelectedCompanyId(editingCard.template_company_id);

      }
    }
  }, [companies, editingCard, userRole, selectedCompanyId]);

  const loadExistingColors = async (tierId: string, card?: MembershipCard) => {
    try {
      // Use the new direct endpoint that doesn't require template ID
      const colorData = await multiTenantMembershipAPI.getTierColors(tierId, selectedCard?.template_company_id);

      if (colorData?.colors) {
        setBackgroundColor(colorData.colors.background_color || '#ffffff');
        setTextColor(colorData.colors.text_color || '#000000');
        setLabelColor(colorData.colors.label_color || '#666666');
        setStripColor(colorData.colors.strip_color || '#0066cc');
      } else {
        // No existing colors found, use defaults but allow editing
        setBackgroundColor('#ffffff');
        setTextColor('#000000');
        setLabelColor('#666666');
        setStripColor('#0066cc');
      }
    } catch (error: any) {
      logger.error('❌ Error loading existing colors:', error);
      logger.error('❌ Error details:', error.response?.data || error.message);
      // Use defaults on error
      setBackgroundColor('#ffffff');
      setTextColor('#000000');
      setLabelColor('#666666');
      setStripColor('#0066cc');
    }
  };

  const getCompanyName = useCallback((companyId?: string) => {
    if (!companyId) return 'Nepoznata kompanija';

    const company = companies.find(c => c.company_id === companyId);

    return company?.company_name || company?.display_name || 'Nepoznata kompanija';
  }, [companies]);

  // Filter cards based on search term and company
  const filteredCards = useMemo(() => {
    let filtered = cards;

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(card =>
        card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.benefits.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by company (admin only)
    if (userRole === 'admin' && filterCompanyId) {
      filtered = filtered.filter(card => card.template_company_id === filterCompanyId);
    }

    return filtered;
  }, [cards, searchTerm, filterCompanyId, userRole]);

  const loadAllTierColors = async (tiers: MembershipCard[]) => {
    const colors: {[tierId: string]: any} = {};

    for (const tier of tiers) {
      try {
        // Use the new direct endpoint that doesn't require template ID
        const colorData = await multiTenantMembershipAPI.getTierColors(tier.id, tier.template_company_id);
        if (colorData?.colors && Object.keys(colorData.colors).length > 0) {
          // Use unique key combining tier_id + company_id to prevent overlap
          const tierKey = `${tier.id}_${tier.template_company_id}`;
          colors[tierKey] = colorData.colors;
        }
      } catch (error) {
        // Ignore errors for tiers without colors
      }
    }

    setTierColors(colors);
  };

  const handleOpenDialog = (card?: MembershipCard) => {

    setEditingCard(card || null);

    // If editing existing card, set the company if admin
    if (card && userRole === 'admin') {
      if (companies.length > 0) {
        let companyToSelect = '';

        // For existing tiers, use the tier's template company
        if (card.template_company_id) {
          companyToSelect = card.template_company_id;
        } else if (user?.company_id) {
          // Fallback to user's company
          companyToSelect = user.company_id;
        } else if (companies[0]?.company_id) {
          // Last fallback to first available company
          companyToSelect = companies[0].company_id;
        }

        setSelectedCompanyId(companyToSelect);
      }
    }

    // Load existing colors if editing
    if (card) {
      setUseCustomColors(true); // For editing, always show colors

      // Create unique key for tier + company combination
      const tierKey = `${card.id}_${card.template_company_id}`;

      // First try to use colors from local cache (faster)
      if (tierColors[tierKey]) {
        setBackgroundColor(tierColors[tierKey].background_color || '#ffffff');
        setTextColor(tierColors[tierKey].text_color || '#000000');
        setLabelColor(tierColors[tierKey].label_color || '#666666');
        setStripColor(tierColors[tierKey].strip_color || '#0066cc');
      } else {
        // Fall back to API call
        loadExistingColors(card.id, card);
      }
    } else {
      // Reset to defaults for new tier
      setBackgroundColor('#ffffff');
      setTextColor('#000000');
      setLabelColor('#666666');
      setStripColor('#0066cc');
      setUseCustomColors(false); // Default to copy template
      // Don't set source tier here - let useEffect handle it when company is selected
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCard(null);
    setSelectedCompanyId('');
    setUseCustomColors(false); // Reset color option
    setSourceTierId(''); // Reset source tier selection
    // Reset color values to defaults
    setBackgroundColor('#ffffff');
    setTextColor('#000000');
    setLabelColor('#666666');
    setStripColor('#0066cc');
  };

  // Fetch companies for admin dropdown
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await multiTenantMembershipAPI.getCompanies();
      setCompanies(response || []);
      setUserRole('customer');
      logger.log('[MembershipCards] Companies loaded:', response);
    } catch (error) {
      logger.error('[MembershipCards] Error fetching companies:', error);
      setCompanies([]);
    }
  }, []);

  // Load companies when dialog opens
  useEffect(() => {
    if (openDialog) {
      fetchCompanies();
    }
  }, [openDialog, fetchCompanies]);

  // Load companies immediately for displaying company names
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Set default source tier when company is selected or cards change
  useEffect(() => {
    if (openDialog && !editingCard && !useCustomColors && cards && cards.length > 0) {
      const filteredCards = cards.filter((card) => {
        // If admin has selected a company, show only tiers from that company
        if (userRole === 'admin' && selectedCompanyId) {
          return card.template_company_id === selectedCompanyId;
        }
        // For regular users, show only tiers from their company
        return card.template_company_id === user?.company_id;
      });

      if (filteredCards.length > 0 && !sourceTierId) {
        setSourceTierId(filteredCards[0].id);
      }
    }
  }, [openDialog, editingCard, useCustomColors, cards, selectedCompanyId, userRole, user?.company_id, sourceTierId]);

  // Helper: Extract and validate form data
  const extractFormData = (formData: FormData) => {
    return {
      name: formData.get('name') as string,
      membership_type: formData.get('membership_type') as string,
      tier_level: Number.parseInt(formData.get('tier_level') as string) || 1,
      benefits: [],
      annual_fee: 0,
      points_per_dollar: 1.0,
      card_template: 'standard',
      has_expiry: true,
      default_validity_months: 12,
      has_points_system: true,
      has_qr_code: false,
      has_barcode: false,
      background_color: formData.get('background_color') as string,
      text_color: formData.get('text_color') as string,
      label_color: formData.get('label_color') as string,
      strip_color: formData.get('strip_color') as string,
    };
  };

  // Helper: Find program for target company
  const findCompanyProgram = async (targetCompanyId: string) => {
    const companyProgramsResponse = await multiTenantMembershipAPI.getCompanyPrograms();
    const companyPrograms = companyProgramsResponse.data || [];

    const targetProgram = companyPrograms.find((program: any) => {
      logger.log(`🔍 Checking program: ${program.name}, company_id: "${program.company_id}" vs target: "${targetCompanyId}"`);
      return program.company_id === targetCompanyId;
    });

    if (!targetProgram) {
      logger.error(`❌ No program found for company ${targetCompanyId}`);
      logger.log('Available programs:', companyPrograms.map((p: any) => ({
        id: p.id,
        name: p.name,
        company_id: p.company_id
      })));
      throw new Error(`Nema dostupnog programa za kompaniju ${targetCompanyId}`);
    }

    return targetProgram;
  };

  // Helper: Build tier data for update
  const buildUpdateTierData = (data: any, targetCompanyId: string, editingCardId: string) => {
    return {
      tier_name: data.name,
      tier_index: data.tier_level,
      ...(targetCompanyId && { company_id: targetCompanyId }),
      timezone: 'Europe/London',
      template_strategy: useCustomColors ? 'create_new' : 'copy_existing',
      ...(editingCardId && { source_tier_id: editingCardId }),
      ...(useCustomColors && {
        template_name: `Updated ${data.name} Template`,
        background_color: backgroundColor,
        text_color: textColor,
        label_color: labelColor,
        strip_color: stripColor
      })
    };
  };

  // Helper: Build tier data for creation
  const buildCreateTierData = (data: any, targetCompanyId: string) => {
    return {
      tier_name: data.name,
      tier_index: data.tier_level,
      ...(targetCompanyId && { company_id: targetCompanyId }),
      timezone: 'Europe/London',
      template_strategy: useCustomColors ? 'create_new' : 'copy_existing',
      ...(sourceTierId && { source_tier_id: sourceTierId }),
      ...(useCustomColors && {
        template_name: `${data.name} Template`,
        background_color: backgroundColor,
        text_color: textColor,
        label_color: labelColor,
        strip_color: stripColor
      })
    };
  };

  const createNewTier = async (data: any) => {
    let targetCompanyId = user?.company_id;
    if (userRole === 'admin' && selectedCompanyId) {
      targetCompanyId = selectedCompanyId;
    }
    const targetProgram = await findCompanyProgram(targetCompanyId!);
    const tierData = buildCreateTierData(data, targetCompanyId!);
    await multiTenantMembershipAPI.createProgramTier(targetProgram.id, tierData);
  };

  const handleTierError = (error: any) => {
    logger.error('❌ ERROR:', error);
    const errorMessage = error.response?.data?.detail || error.message || 'Neuspešno čuvanje tier-a';
    setError(errorMessage);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    // Validate source tier selection for copy strategy
    if (!editingCard && !useCustomColors && !sourceTierId) {
      setError('Molimo odaberite tier za kopiranje template-a.');
      return;
    }

    const data = extractFormData(formData);

    try {
      setLoading(true);

      if (editingCard) {
        // Use program-based tier update
        logger.log('📝 UPDATING TIER:', editingCard.id);

        // Determine target company ID
        let targetCompanyId = editingCard.template_company_id || user?.company_id;
        if (userRole === 'admin' && selectedCompanyId) {
          targetCompanyId = selectedCompanyId;
        }

        logger.log(`📝 Updating tier for company: ${targetCompanyId}`);

        // Find program for target company
        const targetProgram = await findCompanyProgram(targetCompanyId!);
        const programId = targetProgram.id;
        logger.log(`✅ Found program for update: ${targetProgram.name} (${programId})`);

        // Build tier data
        const tierData = buildUpdateTierData(data, targetCompanyId!, editingCard.id);
        logger.log('📝 UPDATING TIER WITH DATA:', JSON.stringify(tierData, null, 2));

        // Call update endpoint
        const result = await multiTenantMembershipAPI.updateProgramTier(programId, editingCard.id, tierData);
        logger.log('📝 Update result:', result);

        // Save tier colors directly
        await saveTierColors(editingCard.id, targetCompanyId!);
        await fetchCards();
        handleCloseDialog();
      } else {
        // Create new tier
        await createNewTier(data);
        await fetchCards();
        handleCloseDialog();
      }
    } catch (error) {
      handleTierError(error);
    } finally {
      setLoading(false);
    }
  };

  const saveTierColors = async (tierId: string, companyId: string) => {
    await multiTenantMembershipAPI.updateTierColorsDirect(tierId, {
      background_color: backgroundColor,
      text_color: textColor,
      label_color: labelColor,
      strip_color: stripColor
    }, companyId);

    const tierKey = `${tierId}_${companyId}`;
    setTierColors(prev => ({
      ...prev,
      [tierKey]: {
        background_color: backgroundColor,
        text_color: textColor,
        label_color: labelColor,
        strip_color: stripColor
      }
    }));
  };

  // Delete functionality
  const handleDeleteClick = (card: MembershipCard) => {
    setDeletingCard(card);
    setDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialog(false);
    setDeletingCard(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCard) return;

    try {
      setDeleting(true);

      const targetCompanyId = deletingCard.template_company_id || user?.company_id;
      const targetProgram = await findCompanyProgram(targetCompanyId!);

      // Call the canonical program-based delete API
      await multiTenantMembershipAPI.deleteProgramTier(targetProgram.id, deletingCard.id);

      // Refresh the tier list
      await fetchCards();

      // Close delete dialog
      setDeleteDialog(false);
      setDeletingCard(null);

      logger.log(`[MembershipCards] Successfully deleted tier: ${deletingCard.name}`);

    } catch (error) {
      logger.error('[MembershipCards] Error deleting tier:', error);

      // Better error message based on error type
      let errorMessage = 'Neuspešno brisanje tier-a. ';
      if (error instanceof Error) {
        errorMessage += `Greška: ${error.message}`;
      } else if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as any;
        errorMessage += `HTTP ${axiosError.response?.status}: ${axiosError.response?.data?.detail || axiosError.message}`;
      } else {
        errorMessage += 'Molimo pokušajte ponovo.';
      }

      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Menu functions
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, card: MembershipCard) => {
    setAnchorEl(event.currentTarget);
    setSelectedCard(card);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCard(null);
  };

  const handleMenuEdit = () => {
    if (selectedCard) {
      handleOpenDialog(selectedCard);
      handleMenuClose();
    }
  };

  const handleMenuDelete = () => {
    if (selectedCard) {
      handleDeleteClick(selectedCard);
      handleMenuClose();
    }
  };

  const getTierColor = (level: number) => {
    const colors = ['#E8F5E8', '#FFF3E0', '#E3F2FD', '#F3E5F5', '#FCE4EC'];
    return colors[level - 1] || '#F5F5F5';
  };

  const getTierIconColor = (level: number): 'primary' | 'warning' | 'success' => {
    if (level <= 2) return 'primary';
    if (level <= 4) return 'warning';
    return 'success';
  };

  const getTierIcon = (level: number) => {
    if (level <= 2) return <PeopleIcon />;
    if (level <= 4) return <StarIcon />;
    return <TrendingUpIcon />;
  };

  // Memoize expensive calculations
  const totalMembers = useMemo(() => {
    return cards.reduce((sum, card) => sum + (card.member_count || 0), 0);
  }, [cards]);

  const activeTiers = useMemo(() => {
    return cards.filter(c => c.status === 'active').length;
  }, [cards]);

  const averageMembersPerTier = useMemo(() => {
    return cards.length > 0 ? Math.round(totalMembers / cards.length) : 0;
  }, [totalMembers, cards.length]);

  return (
    <Box sx={{
      p: { xs: 2, sm: 2.5, md: 3 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    }}>
      <ResponsivePageHeader
        title="Upravljanje Članstvom"
        subtitle={
          user?.role === 'admin'
            ? "🔧 Admin Mode: Upravljajte tier-ovima svih kompanija"
            : "Kreirajte i upravljajte tier-ovima članskih programa"
        }
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <ModernButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
          >
            Novi Tier
          </ModernButton>
        }
      />

      {/* Filters Section */}
      <ModernCard sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            {/* Search Filter */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Pretraži tier-ove po imenu ili benefitima..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchTerm('')}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            {/* Company Filter (Admin Only) */}
            {userRole === 'admin' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Filter po kompaniji</InputLabel>
                  <Select
                    value={filterCompanyId}
                    onChange={(e) => setFilterCompanyId(e.target.value)}
                    label="Filter po kompaniji"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">
                      <em>Sve kompanije</em>
                    </MenuItem>
                    {companies.map((company) => (
                      <MenuItem key={company.company_id} value={company.company_id}>
                        {company.company_name || company.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Results Count */}
            <Grid size={{ xs: 12, md: userRole === 'admin' ? 2 : 6 }}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                {filteredCards.length} od {cards.length} tier-ova
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </ModernCard>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="primary" variant="soft">
                  <BusinessIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {cards?.length || 0}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Ukupno Tier-ova
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="success" variant="soft">
                  <PeopleIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {totalMembers}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Ukupno Članova
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="info" variant="soft">
                  <StarIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="info.main" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {activeTiers}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Aktivni Tier-ovi
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="warning" variant="soft">
                  <TrendingUpIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {averageMembersPerTier}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Avg. po Tier-u
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchCards}>
              🔄 Pokušaj Ponovo
            </Button>
          }
        >
          <Typography variant="body1" fontWeight={600} mb={1}>
            {user?.role === 'admin' ? '🔧 Admin Error' : 'Greška'}
          </Typography>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`program-skeleton-${index}`}>
              <ModernCard>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }} mb={{ xs: 1.5, md: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="60%" height={28} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Box>
                  <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: { xs: 1.5, md: 2 } }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="rectangular" width={80} height={32} />
                  </Box>
                </CardContent>
              </ModernCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tier Cards Grid */}
      {!loading && (
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {filteredCards.map((card, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`${card.template_company_id}-${card.id}-${index}`}>
              <ModernCard hover>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <IconContainer
                        color={getTierIconColor(card.tier_level)}
                        variant="soft"
                      >
                        {getTierIcon(card.tier_level)}
                      </IconContainer>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                          >
                            {card.name}
                          </Typography>
                          {user?.role === 'admin' && card.company_name && (
                            <Chip
                              label={card.company_name}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            fontSize: { xs: '0.875rem', md: '0.925rem' }
                          }}
                        >
                          {card.company_name || getCompanyName(card.template_company_id)}
                        </Typography>
                        <Chip
                          label={`Nivo ${card.tier_level}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />

                        {/* Color indicator for saved colors */}
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {(() => {
                            const tierKey = `${card.id}_${card.template_company_id}`;
                            const cardColors = tierColors[tierKey];
                            return cardColors ? (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                  Boje:
                                </Typography>
                                {cardColors.background_color && (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: cardColors.background_color,
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                )}
                                {cardColors.text_color && (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: cardColors.text_color,
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                )}
                                {cardColors.label_color && (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: cardColors.label_color,
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                )}
                                {cardColors.strip_color && (
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: cardColors.strip_color,
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Standardne boje
                              </Typography>
                            );
                          })()}
                        </Box>
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, card)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: getTierColor(card.tier_level),
                    mb: 3
                  }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Članova
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {card.member_count}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Poeni za pristup
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {card.points_threshold}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>


                </CardContent>
              </ModernCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && filteredCards.length === 0 && (
        <ModernCard>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <StarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                {(() => {
                  if (cards.length === 0) return 'Nema kreiranih tier-ova';
                  if (searchTerm || filterCompanyId) return 'Nema tier-ova koji odgovaraju filterima';
                  return 'Nema tier-ova za prikaz';
                })()}
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 400 }}>
                {cards.length === 0
                  ? 'Kreirajte prvi tier da počnete sa upravljanjem članskim programom.'
                  : 'Pokušajte da promenite filtere ili dodajte novi tier.'
                }
              </Typography>
              {cards.length === 0 && (
                <ModernButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{ mt: 2 }}
                >
                  Kreiraj Prvi Tier
                </ModernButton>
              )}
              {(searchTerm || filterCompanyId) && cards.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterCompanyId('');
                  }}
                  sx={{ mt: 2 }}
                >
                  Očisti Filtere
                </Button>
              )}
            </Box>
          </CardContent>
        </ModernCard>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleMenuEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Uredi</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Obriši</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 3
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="span" fontSize="1.25rem" fontWeight={600}>
            {editingCard ? 'Uredi Tier' : 'Kreiraj Novi Tier'}
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block" mt={0.5}>
            {editingCard ? 'Izmenite postojeći tier članskog programa' : 'Dodajte novi tier u članský program'}
          </Typography>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ModernTextField
                  name="name"
                  label="Naziv Tier-a"
                  fullWidth
                  required
                  defaultValue={editingCard?.name || ''}
                  placeholder="npr. Silver Tier"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <ModernTextField
                  name="tier_level"
                  label="Nivo Tier-a"
                  type="number"
                  fullWidth
                  required
                  defaultValue={editingCard?.tier_level || 1}
                  slotProps={{
                    htmlInput: { min: 1, max: 10 }
                  }}
                />
              </Grid>



              {/* Template & Color Options - Only show for new tiers */}
              {!editingCard && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Template Opcije
                  </Typography>

                  <FormControl component="fieldset">
                    <RadioGroup
                      value={useCustomColors ? 'custom' : 'copy'}
                      onChange={(e) => setUseCustomColors(e.target.value === 'custom')}
                    >
                      <FormControlLabel
                        value="copy"
                        control={<Radio />}
                        label="📋 Kopiraj postojeći template (iste boje)"
                      />
                      <FormControlLabel
                        value="custom"
                        control={<Radio />}
                        label="🎨 Prilagodi boje"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {/* Source Tier Selection - Only show when copy strategy is selected */}
              {!editingCard && !useCustomColors && cards && cards.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    Odaberite Source Tier
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel>Tier za kopiranje template-a</InputLabel>
                    <Select
                      value={sourceTierId}
                      onChange={(e) => setSourceTierId(e.target.value)}
                      label="Tier za kopiranje template-a"
                    >
                      {cards
                        .filter((card) => {
                          // If admin has selected a company, show only tiers from that company
                          if (userRole === 'admin' && selectedCompanyId) {
                            return card.template_company_id === selectedCompanyId;
                          }
                          // For regular users, show only tiers from their company
                          return card.template_company_id === user?.company_id;
                        })
                        .map((card) => (
                          <MenuItem key={card.id} value={card.id}>
                            {card.name} (Level {card.tier_level}) - {card.company_name || 'Unknown Company'}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Color Customization Section */}
              {(useCustomColors || editingCard) && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    {editingCard ? 'Boje Tier-a' : 'Prilagodite boje za vaš tier'}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <ColorSelector
                        name="background_color"
                        label="Pozadina"
                        value={backgroundColor}
                        onChange={setBackgroundColor}
                      />
                    </Grid>

                    <Grid size={{ xs: 6, md: 3 }}>
                      <ColorSelector
                        name="text_color"
                        label="Tekst"
                        value={textColor}
                        onChange={setTextColor}
                      />
                    </Grid>

                    <Grid size={{ xs: 6, md: 3 }}>
                      <ColorSelector
                        name="label_color"
                        label="Labela"
                        value={labelColor}
                        onChange={setLabelColor}
                      />
                    </Grid>

                    <Grid size={{ xs: 6, md: 3 }}>
                      <ColorSelector
                        name="strip_color"
                        label="Traka"
                        value={stripColor}
                        onChange={setStripColor}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Company Selection for Admin Users */}
              {userRole === 'admin' && companies.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                    <InputLabel id="company-select-label">Izaberite kompaniju</InputLabel>
                    <Select
                      labelId="company-select-label"
                      value={selectedCompanyId || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value && value !== 'undefined' && value !== 'null') {
                          setSelectedCompanyId(String(value));
                        }
                      }}
                      label="Izaberite kompaniju"
                      displayEmpty
                    >
                      {companies
                        .filter((company) => company.company_id != null && company.company_id !== undefined)
                        .map((company) => (
                        <MenuItem key={company.company_id} value={String(company.company_id)}>
                          {company.company_name || company.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Otkaži
            </Button>
            <ModernButton
              type="submit"
              variant="contained"
              disabled={loading}
              loading={loading}
              gradient
            >
              {editingCard ? 'Sačuvaj Izmene' : 'Kreiraj Tier'}
            </ModernButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Potvrda Brisanja
        </DialogTitle>
        <DialogContent>
          <Typography>
            Da li ste sigurni da želite da obrišete tier "{deletingCard?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Ova akcija je nepovratna i trajno će ukloniti tier iz sistema.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleDeleteCancel} variant="outlined">
            Otkaži
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Brišem...' : 'Obriši'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembershipCards;
